import React from 'react';
import { useRoutes, useLocation, Navigate } from "react-router-dom";
import { Capacitor } from '@capacitor/core';
import LoginForm from "./components/loginForm";
import Home from "./components/Home";
import About from './About';
import Contact from './Contact';
import Benefits from './components/Benefits';
import PrivacyPolicy from './components/PrivacyPolicy';
import CookiePolicy from './components/CookiePolicy';
import TermsOfService from './components/TermsOfService';
import SafetyGuidelines from './components/SafetyGuidelines';
import Developer from './components/Developer/Developer';
import PublishPost from './components/PublishPost';
import SendPackage from './components/SendPackage';
import SettingsForm from './components/SettingsForm';
import MyProfilePage from './components/MyProfile';
import ActivityPage from './components/MyProfile/ActivityPage';
import PostDetails from './components/PostDetails';
import SubscriptionPlans from './components/payment/SubscriptionPlans'; // Import the SubscriptionPlans component
import Footer from './components/Footer'; // Import the Footer component
import ProtectedRoute from './components/ProtectedRoute'; // Import the ProtectedRoute component
import GuestRoute from './components/GuestRoute'; // Import the GuestRoute component
import { ChatProvider } from './components/Chat/ChatContext';
import { AuthProvider } from './components/Provider';
import { ToastProvider } from './components/hooks/ToastContext'; // Update this import
import { NotificationProvider } from './components/Notifications';
import { ToastContainer } from './components/Templates';
import ProfileCompleteContainer from './components/ProfileComplete/ProfileCompleteContainer';
import ResetPassword from './components/ResetPassword'; // Import ResetPassword component
import UnsubscribePage from './components/UnsubscribePage'; // Import UnsubscribePage component
import ChatV2 from './components/Chat/ChatV2'; // Import ChatV2 at app level
import Header from './headerL'; // Import Header at app level
import NativeTopBar from './components/NativeTopBar'; // Native (Capacitor) app bar
import CompletionListener from './components/CompletionListener'; // Import CompletionListener for post renewals
import BottomNavBar from './components/BottomNavBar'; // Import BottomNavBar for mobile navigation
import CookieBanner from './components/common/CookieBanner'; // Import Cookie Banner for GDPR compliance

// Import feature flags
import { FEATURES } from './config/features';

// Conditionally import Events components only if feature is enabled
let EventsPage, EventDetailsPage, CreateEventPage, EventRideForm;
if (FEATURES.EVENTS_ENABLED) {
  EventsPage = require('./components/Events').default;
  EventDetailsPage = require('./components/Events/EventDetailsPage').default;
  CreateEventPage = require('./components/Events/CreateEventPage').default;
  EventRideForm = require('./components/Events/components/EventRide').EventRideForm;
}

// Conditionally import Groups components only if feature is enabled
let GroupsPage, GroupDetailsPage, CreateGroupPage, CreateGroupRide, GroupRideDetailsPage, CreateGroupDiscussion, GroupDiscussionDetailsPage, GroupMembersManagement, EditGroupPage;
if (FEATURES.GROUPS_ENABLED) {
  const GroupsModule = require('./components/Groups');
  GroupsPage = GroupsModule.GroupsPage;
  GroupDetailsPage = GroupsModule.GroupDetailsPage;
  CreateGroupPage = GroupsModule.CreateGroupPage;
  
  const GroupRideModule = require('./components/Groups/components/GroupRide');
  CreateGroupRide = GroupRideModule.CreateGroupRide;
  GroupRideDetailsPage = GroupRideModule.GroupRideDetailsPage;
  
  const GroupDiscussionModule = require('./components/Groups/components/GroupDiscussion');
  CreateGroupDiscussion = GroupDiscussionModule.CreateGroupDiscussion;
  GroupDiscussionDetailsPage = GroupDiscussionModule.GroupDiscussionDetailsPage;
  
  const GroupManagementModule = require('./components/Groups/components/GroupManagement');
  GroupMembersManagement = GroupManagementModule.GroupMembersManagement;
  EditGroupPage = GroupManagementModule.EditGroupPage;
}

function App() {
  const location = useLocation();
  
  // Check if current page is login page, reset password, or profile completion flow to conditionally show header/footer
  const isLoginPage = location.pathname === '/login';
  const isProfileCompletePage = location.pathname === '/complete-profile';
  const isResetPasswordPage = location.pathname === '/reset-password';
  
  // In the native app the login screen is a dedicated full-screen view (no web
  // header/footer) — see LoginForm.css native overrides.
  const isNative = typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform();

  // Hide header and footer for profile completion and reset password pages
  // (and for login in the native app).
  const shouldHideHeaderFooter = isProfileCompletePage || isResetPasswordPage || (isNative && isLoginPage);

  // Native status bar follows the screen: the dark full-screen auth pages get a
  // dark bar with light icons; every other screen (light app bar) gets a light
  // bar with dark icons. Avoids the near-black status bar on the login screen.
  React.useEffect(() => {
    if (!isNative) return;
    const darkTop = isLoginPage || isProfileCompletePage || isResetPasswordPage;
    (async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        await StatusBar.setStyle({ style: darkTop ? Style.Dark : Style.Light });
        try { await StatusBar.setBackgroundColor({ color: darkTop ? '#4a565c' : '#eef3ec' }); } catch (e) { /* edge-to-edge ignores this */ }
      } catch (e) { /* plugin optional */ }
    })();
  }, [isNative, isLoginPage, isProfileCompletePage, isResetPasswordPage]);
  
  // Debug logging to help troubleshoot
  console.log('App.js Debug:');
  console.log('Current pathname:', location.pathname);
  console.log('isLoginPage:', isLoginPage);
  console.log('isProfileCompletePage:', isProfileCompletePage);
  console.log('shouldHideHeaderFooter:', shouldHideHeaderFooter);

  // Create base routes
  const routes = [
    // Public routes (no authentication required)
    { path: '/', element: <Home /> },
    { path: '/home', element: <Home /> },
    { path: '/login', element: <GuestRoute><LoginForm /></GuestRoute> },
    { path: '/reset-password', element: <ResetPassword /> }, // Password reset page - no auth wrapper needed
    { path: '/unsubscribe/:token', element: <UnsubscribePage /> }, // Newsletter unsubscribe page - no auth required
    { path: '/Contact', element: <Contact /> },
    { path: '/About', element: <About /> },
    { path: '/benefits', element: <Benefits /> },
    { path: '/privacy', element: <PrivacyPolicy /> },
    { path: '/cookies', element: <CookiePolicy /> },
    { path: '/terms', element: <TermsOfService /> },
    { path: '/safety', element: <SafetyGuidelines /> },
    // Subscription pages — redirect home while payments are paused (everyone unlimited).
    { path: '/subscription-plans', element: FEATURES.PAYMENTS_ENABLED ? <SubscriptionPlans /> : <Navigate to="/home" replace /> },
    { path: '/subscription', element: FEATURES.PAYMENTS_ENABLED ? <SubscriptionPlans /> : <Navigate to="/home" replace /> },
    
    // Protected routes (authentication required)
    { path: '/complete-profile', element: <ProtectedRoute><ProfileCompleteContainer /></ProtectedRoute> },
    { path: '/PublishPost', element: <ProtectedRoute><PublishPost /></ProtectedRoute> },
    { path: '/SendPackage', element: <ProtectedRoute><SendPackage /></ProtectedRoute> },
    { path: '/MyprofileForm', element: <ProtectedRoute><MyProfilePage /></ProtectedRoute> },
    { path: '/activity', element: <ProtectedRoute><ActivityPage /></ProtectedRoute> },
    { path: '/Settings', element: <ProtectedRoute><SettingsForm /></ProtectedRoute> },
    
    // Post/Package details routes (protected)
    { path: '/post/:id', element: <ProtectedRoute><PostDetails type="post" /></ProtectedRoute> },
    { path: '/package/:id', element: <ProtectedRoute><PostDetails type="package" /></ProtectedRoute> },
    { path: '/request/:id', element: <ProtectedRoute><PostDetails type="request" /></ProtectedRoute> },
    
    // Developer route (protected and restricted)
    { path: '/developer', element: <ProtectedRoute><Developer/></ProtectedRoute> }
  ];

  // Conditionally add Events routes if feature is enabled
  if (FEATURES.EVENTS_ENABLED && EventsPage && EventDetailsPage && CreateEventPage && EventRideForm) {
    routes.push(
      { path: '/events', element: <ProtectedRoute><EventsPage /></ProtectedRoute> },
      { path: '/events/create', element: <ProtectedRoute><CreateEventPage /></ProtectedRoute> },
      { path: '/events/:id', element: <ProtectedRoute><EventDetailsPage /></ProtectedRoute> },
      { path: '/events/:id/ride', element: <ProtectedRoute><EventRideForm /></ProtectedRoute> }
    );
  } else {
    // Redirect all events routes to home when feature is disabled
    routes.push(
      { path: '/events/*', element: <Navigate to="/" replace /> }
    );
  }

  // Conditionally add Groups routes if feature is enabled
  if (FEATURES.GROUPS_ENABLED && GroupsPage && GroupDetailsPage && CreateGroupPage) {
    routes.push(
      { path: '/groups', element: <ProtectedRoute><GroupsPage /></ProtectedRoute> },
      { path: '/groups/create', element: <ProtectedRoute><CreateGroupPage /></ProtectedRoute> },
      { path: '/groups/:id', element: <ProtectedRoute><GroupDetailsPage /></ProtectedRoute> },
      { path: '/groups/:id/edit', element: <ProtectedRoute><EditGroupPage /></ProtectedRoute> },
      { path: '/groups/:id/members', element: <ProtectedRoute><GroupMembersManagement /></ProtectedRoute> },
      { path: '/groups/:id/ride/create', element: <ProtectedRoute><CreateGroupRide /></ProtectedRoute> },
      { path: '/groups/:id/ride/:rideId', element: <ProtectedRoute><GroupRideDetailsPage /></ProtectedRoute> },
      { path: '/groups/:id/discussion/create', element: <ProtectedRoute><CreateGroupDiscussion /></ProtectedRoute> },
      { path: '/groups/:id/discussion/:discussionId', element: <ProtectedRoute><GroupDiscussionDetailsPage /></ProtectedRoute> }
    );
  } else {
    // Redirect all groups routes to home when feature is disabled
    routes.push(
      { path: '/groups/*', element: <Navigate to="/" replace /> }
    );
  }

  const element = useRoutes(routes);

  return (
    <ToastProvider>
      <AuthProvider>
        <NotificationProvider>
          <ChatProvider>
            {/* Header — native app bar in the Capacitor app, full web header on
                the web. Hidden during login / profile completion / reset. */}
            {!shouldHideHeaderFooter && (isNative ? <NativeTopBar /> : <Header />)}
            <div className={`page-container${isNative && !shouldHideHeaderFooter ? ' has-native-topbar' : ''}`}>
              {isNative ? (
                <div className="native-page-transition" key={location.pathname}>
                  {element}
                </div>
              ) : (
                element
              )}
              {/* Footer - hidden during login and profile completion */}
              {!shouldHideHeaderFooter && <Footer />}
            </div>
            <ToastContainer />
            {/* Cookie Consent Banner - GDPR compliance, shown globally */}
            <CookieBanner />
            {/* Bottom Navigation Bar - shown on mobile devices (600px and below) */}
            {!isLoginPage && !isProfileCompletePage && !isResetPasswordPage && <BottomNavBar />}
            {/* Chat component - hidden during login, reset password, and profile completion for focused experience */}
            {!isLoginPage && !isProfileCompletePage && !isResetPasswordPage && <ChatV2 />}
            {/* Completion listener for post renewals - active when user is logged in */}
            {!isLoginPage && !isProfileCompletePage && !isResetPasswordPage && <CompletionListener />}
          </ChatProvider>
        </NotificationProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;